import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useSnackbar } from '../contexts/SnackbarContext.tsx';
import { balanceApi, BalanceEntry, PaidOutEntry, BusserTipEntry, EventServerBartenderTipEntry, NegativeCashOutEntry, RegisterEntry, BarTipEntry, ExpoTipEntry } from '../services/balanceService.ts';
import { employeeApi, Employee } from '../services/employeeService.ts';
import { tipoutApi } from '../services/tipoutService.ts';

interface BalanceRow {
  id: string;
  employeeId: number;
  employeeName: string;
  type: 'server' | 'host' | 'bar' | 'manager';
  cashDue: number;
  netSales: number;
}

// Fixed registers tracked on the New Balance form, each with a standard default
// (starting/float) amount; the only thing entered day-to-day is the close-out amount.
const REGISTER_DEFAULTS: Array<{ key: string; label: string; defaultAmount: number }> = [
  { key: 'bar_am', label: 'Bar AM', defaultAmount: 150 },
  { key: 'bar_pm', label: 'Bar PM', defaultAmount: 150 },
  { key: 'host', label: 'Host', defaultAmount: 250 },
  { key: 'keno', label: 'Keno', defaultAmount: 400 },
];

const TipsPaidCashOutsPage: React.FC = () => {
  const { sessionToken, user } = useAuth();
  const isAdmin = user?.type === 'admin';
  const { showSuccess, showError } = useSnackbar();
  const [searchParams] = useSearchParams();
  
  // Current date state
  const [currentDate, setCurrentDate] = useState<string>('');
  const [dateLoading, setDateLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'pending' | 'saving' | 'saved' | 'error'>('idle');
  const [autoSaveError, setAutoSaveError] = useState('');
  const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitialLoadRef = useRef(true);

  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    };
  }, []);

  // Employees data
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);

  // Table data
  const [amData, setAmData] = useState<BalanceRow[]>([]);
  const [pmData, setPmData] = useState<BalanceRow[]>([]);
  const [paidOuts, setPaidOuts] = useState<PaidOutEntry[]>([]);
  const [busserTips, setBusserTips] = useState<BusserTipEntry[]>([]);
  const [eventServersBartenderTips, setEventServersBartenderTips] = useState<EventServerBartenderTipEntry[]>([]);
  const [negativeCashOuts, setNegativeCashOuts] = useState<NegativeCashOutEntry[]>([]);
  const [barTips, setBarTips] = useState<BarTipEntry[]>([]);
  const [expoTips, setExpoTips] = useState<ExpoTipEntry[]>([]);
  // Daily (AM+PM, excluding Event1/Event2) Bus/Bar/Expo tip-out totals pulled from the
  // Tip Out Log page for the same date, shown next to each tips table's title.
  const [tipOutTotals, setTipOutTotals] = useState({ bus: 0, bar: 0, expo: 0 });
  // Keyed by REGISTER_DEFAULTS[].key; each value is the day's close-out amount (as text,
  // same free-typing pattern as the other dollar fields on this page).
  const [registerForm, setRegisterForm] = useState<Record<string, string>>(
    REGISTER_DEFAULTS.reduce((acc, r) => ({ ...acc, [r.key]: '' }), {} as Record<string, string>)
  );
  // Keyed by REGISTER_DEFAULTS[].key; each value is the employeeId assigned to that register.
  const [registerEmployeeForm, setRegisterEmployeeForm] = useState<Record<string, string>>(
    REGISTER_DEFAULTS.reduce((acc, r) => ({ ...acc, [r.key]: '' }), {} as Record<string, string>)
  );

  // Form states
  const [paidOutForm, setPaidOutForm] = useState({ description: '', cashDue: '' });
  const [busserTipForm, setBusserTipForm] = useState({ busser_name: '', tips: '' });
  const [eventServerBartenderTipForm, setEventServerBartenderTipForm] = useState({ event_server_bartender: '', pooled_tips: '' });
  const [negativeCashOutForm, setNegativeCashOutForm] = useState({ took_from: '', negative_cash_outs: '' });
  const [barTipForm, setBarTipForm] = useState({ bartender_name: '', tip_amount: '' });
  const [expoTipForm, setExpoTipForm] = useState({ expo_name: '', tip_amount: '' });

  // Editing states
  const [editingPaidOut, setEditingPaidOut] = useState<number | null>(null);
  const [editingBusserTip, setEditingBusserTip] = useState<number | null>(null);
  const [editingEventServerBartenderTip, setEditingEventServerBartenderTip] = useState<number | null>(null);
  const [editingNegativeCashOut, setEditingNegativeCashOut] = useState<number | null>(null);
  const [editingBarTip, setEditingBarTip] = useState<number | null>(null);
  const [editingExpoTip, setEditingExpoTip] = useState<number | null>(null);

  // Temp edit data
  const [tempPaidOut, setTempPaidOut] = useState<Partial<PaidOutEntry> | null>(null);
  const [tempBusserTip, setTempBusserTip] = useState<Partial<BusserTipEntry> | null>(null);
  const [tempEventServerBartenderTip, setTempEventServerBartenderTip] = useState<Partial<EventServerBartenderTipEntry> | null>(null);
  const [tempNegativeCashOut, setTempNegativeCashOut] = useState<Partial<NegativeCashOutEntry> | null>(null);
  const [tempBarTip, setTempBarTip] = useState<Partial<BarTipEntry> | null>(null);
  const [tempExpoTip, setTempExpoTip] = useState<Partial<ExpoTipEntry> | null>(null);

  // Fetch employees
  const fetchEmployees = async () => {
    if (!sessionToken) return;
    
    try {
      setEmployeesLoading(true);
      const response = await employeeApi.getEmployees({ page: 1, limit: 1000 }, sessionToken);
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
      showError('Failed to fetch employees');
    } finally {
      setEmployeesLoading(false);
    }
  };

  // Fetch current date from API
  const fetchCurrentDate = async () => {
    if (!sessionToken) return;
    
    try {
      setDateLoading(true);
      const response = await balanceApi.getCurrentDate(sessionToken);
      setCurrentDate(response.date);
      return response.date;
    } catch (error) {
      console.error('Error fetching current date:', error);
      showError('Failed to fetch current date');
      return null;
    } finally {
      setDateLoading(false);
    }
  };

  // Format date string (YYYY-MM-DD) to locale string
  // Parse the date components and create a date in local timezone to avoid timezone shift
  const formatDateString = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      timeZone: 'UTC',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Fetch the day's Bus/Bar/Expo tip-out totals (AM+PM only) from the Tip Out Log page,
  // for display next to the matching tips table's title. Defaults to $0.00 on any error
  // or when there are no logs for the date.
  const fetchTipOutTotals = async (date: string) => {
    if (!sessionToken || !date) return;
    try {
      const logs = await tipoutApi.getDateLogs(sessionToken, date);
      const dailyLogs = logs.filter(log => log.eventType === 'daily');
      setTipOutTotals({
        bus: dailyLogs.reduce((sum, log) => sum + (Number(log.bus) || 0), 0),
        bar: dailyLogs.reduce((sum, log) => sum + (Number(log.bar) || 0), 0),
        expo: dailyLogs.reduce((sum, log) => sum + (Number(log.expo) || 0), 0),
      });
    } catch (error) {
      console.error('Error fetching tip out totals:', error);
      setTipOutTotals({ bus: 0, bar: 0, expo: 0 });
    }
  };

  // Fetch balance data
  const fetchBalanceData = async (date: string) => {
    if (!sessionToken || !date) return;

    // The data this fetch loads is from the server, not a user edit - don't auto-save it.
    isInitialLoadRef.current = true;
    fetchTipOutTotals(date);

    try {
      setDataLoading(true);
      const data = await balanceApi.getBalanceByDate(sessionToken, date);
      
      // Convert balance data to rows
      const amRows: BalanceRow[] = data.balance
        .filter(b => b.shift === 'AM')
        .map((b, idx) => ({
          id: `am-${b.id || idx}`,
          employeeId: b.employeeId,
          employeeName: employees.find(e => e.id === b.employeeId)?.name || `Employee ${b.employeeId}`,
          type: b.type,
          cashDue: Number(b.cashDue) || 0,
          netSales: Number(b.netSales) || 0,
        }));
      
      const pmRows: BalanceRow[] = data.balance
        .filter(b => b.shift === 'PM')
        .map((b, idx) => ({
          id: `pm-${b.id || idx}`,
          employeeId: b.employeeId,
          employeeName: employees.find(e => e.id === b.employeeId)?.name || `Employee ${b.employeeId}`,
          type: b.type,
          cashDue: Number(b.cashDue) || 0,
          netSales: Number(b.netSales) || 0,
        }));

      setAmData(amRows);
      setPmData(pmRows);
      setPaidOuts(data.paid_outs || []);
      setBusserTips(data.busser_tips || []);
      setEventServersBartenderTips(data.event_servers_bartender_tips || []);
      setNegativeCashOuts(data.negative_cash_outs || []);
      setBarTips(data.bar_tips || []);
      setExpoTips(data.expo_tips || []);

      const registerMap = REGISTER_DEFAULTS.reduce((acc, r) => ({ ...acc, [r.key]: '' }), {} as Record<string, string>);
      const registerEmployeeMap = REGISTER_DEFAULTS.reduce((acc, r) => ({ ...acc, [r.key]: '' }), {} as Record<string, string>);
      (data.register || []).forEach((r: RegisterEntry) => {
        registerMap[r.registerType] = String(Number(r.closeOutAmount) || 0);
        registerEmployeeMap[r.registerType] = r.employeeId ? String(r.employeeId) : '';
      });
      setRegisterForm(registerMap);
      setRegisterEmployeeForm(registerEmployeeMap);
    } catch (error) {
      console.error('Error fetching balance data:', error);
      // Don't show error if no data exists for the date
    } finally {
      setDataLoading(false);
    }
  };

  // Shift a YYYY-MM-DD date string by a number of days without local-timezone drift.
  const shiftDate = (dateStr: string, days: number): string => {
    const d = new Date(`${dateStr}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
  };

  // Used by the PREV/NEXT buttons on the Busser/Bar/EXPO Tips tables to browse the
  // whole form's data a day at a time (these tables don't have their own date - the
  // entire balance record is scoped to one date).
  const handleDateNav = (direction: 'prev' | 'next') => {
    if (!currentDate || dateLoading || dataLoading) return;
    const newDate = shiftDate(currentDate, direction === 'prev' ? -1 : 1);
    setCurrentDate(newDate);
    fetchBalanceData(newDate);
  };

  // Load data on mount
  useEffect(() => {
    if (sessionToken) {
      fetchEmployees();
    }
  }, [sessionToken]);

  useEffect(() => {
    if (sessionToken && employees.length > 0) {
      // Check if date is provided in query params
      const dateParam = searchParams.get('date');
      if (dateParam) {
        setCurrentDate(dateParam);
        fetchBalanceData(dateParam);
      } else {
        fetchCurrentDate().then(date => {
          if (date) {
            fetchBalanceData(date);
          }
        });
      }
    }
  }, [sessionToken, employees.length, searchParams]);

  // Add row handlers
  const handleAddPaidOut = () => {
    if (!paidOutForm.description || !paidOutForm.cashDue) return;
    
    const newEntry: PaidOutEntry = {
      id: Date.now(),
      description: paidOutForm.description,
      cashDue: parseFloat(paidOutForm.cashDue),
    };
    
    setPaidOuts([...paidOuts, newEntry]);
    setPaidOutForm({ description: '', cashDue: '' });
  };

  const handleAddBusserTip = () => {
    if (!busserTipForm.busser_name || !busserTipForm.tips) return;
    
    const newEntry: BusserTipEntry = {
      id: Date.now(),
      busserName: busserTipForm.busser_name,
      tips: parseFloat(busserTipForm.tips),
    };
    
    setBusserTips([...busserTips, newEntry]);
    setBusserTipForm({ busser_name: '', tips: '' });
  };

  const handleAddBarTip = () => {
    if (!barTipForm.bartender_name || !barTipForm.tip_amount) return;

    const newEntry: BarTipEntry = {
      id: Date.now(),
      bartenderName: barTipForm.bartender_name,
      tipAmount: parseFloat(barTipForm.tip_amount),
    };

    setBarTips([...barTips, newEntry]);
    setBarTipForm({ bartender_name: '', tip_amount: '' });
  };

  const handleAddExpoTip = () => {
    if (!expoTipForm.expo_name || !expoTipForm.tip_amount) return;

    const newEntry: ExpoTipEntry = {
      id: Date.now(),
      expoName: expoTipForm.expo_name,
      tipAmount: parseFloat(expoTipForm.tip_amount),
    };

    setExpoTips([...expoTips, newEntry]);
    setExpoTipForm({ expo_name: '', tip_amount: '' });
  };

  const handleAddEventServerBartenderTip = () => {
    if (!eventServerBartenderTipForm.event_server_bartender || !eventServerBartenderTipForm.pooled_tips) return;
    
    const newEntry: EventServerBartenderTipEntry = {
      id: Date.now(),
      eventServerBartender: eventServerBartenderTipForm.event_server_bartender,
      pooledTips: parseFloat(eventServerBartenderTipForm.pooled_tips),
    };
    
    setEventServersBartenderTips([...eventServersBartenderTips, newEntry]);
    setEventServerBartenderTipForm({ event_server_bartender: '', pooled_tips: '' });
  };

  const handleAddNegativeCashOut = () => {
    if (!negativeCashOutForm.took_from || !negativeCashOutForm.negative_cash_outs) return;
    
    const newEntry: NegativeCashOutEntry = {
      id: Date.now(),
      tookFrom: negativeCashOutForm.took_from,
      negativeCashOuts: parseFloat(negativeCashOutForm.negative_cash_outs),
    };
    
    setNegativeCashOuts([...negativeCashOuts, newEntry]);
    setNegativeCashOutForm({ took_from: '', negative_cash_outs: '' });
  };

  // Edit handlers for simple tables
  const handleStartEditPaidOut = (entry: PaidOutEntry) => {
    setEditingPaidOut(entry.id || null);
    setTempPaidOut({ ...entry });
  };

  const handleStartEditBusserTip = (entry: BusserTipEntry) => {
    setEditingBusserTip(entry.id || null);
    setTempBusserTip({ ...entry });
  };

  const handleStartEditBarTip = (entry: BarTipEntry) => {
    setEditingBarTip(entry.id || null);
    setTempBarTip({ ...entry });
  };

  const handleStartEditExpoTip = (entry: ExpoTipEntry) => {
    setEditingExpoTip(entry.id || null);
    setTempExpoTip({ ...entry });
  };

  const handleStartEditEventServerBartenderTip = (entry: EventServerBartenderTipEntry) => {
    setEditingEventServerBartenderTip(entry.id || null);
    setTempEventServerBartenderTip({ ...entry });
  };

  const handleStartEditNegativeCashOut = (entry: NegativeCashOutEntry) => {
    setEditingNegativeCashOut(entry.id || null);
    setTempNegativeCashOut({ ...entry });
  };

  const handleSaveEditPaidOut = () => {
    if (editingPaidOut === null || !tempPaidOut) return;
    setPaidOuts(paidOuts.map(entry => entry.id === editingPaidOut ? { ...tempPaidOut as PaidOutEntry, id: entry.id } : entry));
    setEditingPaidOut(null);
    setTempPaidOut(null);
  };

  const handleSaveEditBusserTip = () => {
    if (editingBusserTip === null || !tempBusserTip) return;
    setBusserTips(busserTips.map(entry => entry.id === editingBusserTip ? { ...tempBusserTip as BusserTipEntry, id: entry.id } : entry));
    setEditingBusserTip(null);
    setTempBusserTip(null);
  };

  const handleSaveEditBarTip = () => {
    if (editingBarTip === null || !tempBarTip) return;
    setBarTips(barTips.map(entry => entry.id === editingBarTip ? { ...tempBarTip as BarTipEntry, id: entry.id } : entry));
    setEditingBarTip(null);
    setTempBarTip(null);
  };

  const handleSaveEditExpoTip = () => {
    if (editingExpoTip === null || !tempExpoTip) return;
    setExpoTips(expoTips.map(entry => entry.id === editingExpoTip ? { ...tempExpoTip as ExpoTipEntry, id: entry.id } : entry));
    setEditingExpoTip(null);
    setTempExpoTip(null);
  };

  const handleSaveEditEventServerBartenderTip = () => {
    if (editingEventServerBartenderTip === null || !tempEventServerBartenderTip) return;
    setEventServersBartenderTips(eventServersBartenderTips.map(entry => entry.id === editingEventServerBartenderTip ? { ...tempEventServerBartenderTip as EventServerBartenderTipEntry, id: entry.id } : entry));
    setEditingEventServerBartenderTip(null);
    setTempEventServerBartenderTip(null);
  };

  const handleSaveEditNegativeCashOut = () => {
    if (editingNegativeCashOut === null || !tempNegativeCashOut) return;
    setNegativeCashOuts(negativeCashOuts.map(entry => entry.id === editingNegativeCashOut ? { ...tempNegativeCashOut as NegativeCashOutEntry, id: entry.id } : entry));
    setEditingNegativeCashOut(null);
    setTempNegativeCashOut(null);
  };


  const handleCancelEditPaidOut = () => {
    setEditingPaidOut(null);
    setTempPaidOut(null);
  };

  const handleCancelEditBusserTip = () => {
    setEditingBusserTip(null);
    setTempBusserTip(null);
  };

  const handleCancelEditBarTip = () => {
    setEditingBarTip(null);
    setTempBarTip(null);
  };

  const handleCancelEditExpoTip = () => {
    setEditingExpoTip(null);
    setTempExpoTip(null);
  };

  const handleCancelEditEventServerBartenderTip = () => {
    setEditingEventServerBartenderTip(null);
    setTempEventServerBartenderTip(null);
  };

  const handleCancelEditNegativeCashOut = () => {
    setEditingNegativeCashOut(null);
    setTempNegativeCashOut(null);
  };

  // Delete handlers
  const handleDeletePaidOut = (id?: number) => {
    setPaidOuts(paidOuts.filter(entry => entry.id !== id));
  };

  const handleDeleteBarTip = (id?: number) => {
    setBarTips(barTips.filter(entry => entry.id !== id));
  };

  const handleDeleteExpoTip = (id?: number) => {
    setExpoTips(expoTips.filter(entry => entry.id !== id));
  };

  const handleDeleteBusserTip = (id?: number) => {
    setBusserTips(busserTips.filter(entry => entry.id !== id));
  };

  const handleDeleteEventServerBartenderTip = (id?: number) => {
    setEventServersBartenderTips(eventServersBartenderTips.filter(entry => entry.id !== id));
  };

  const handleDeleteNegativeCashOut = (id?: number) => {
    setNegativeCashOuts(negativeCashOuts.filter(entry => entry.id !== id));
  };

  // Save data
  // Shared save path for both the manual "Save" button and auto-save. `silent`
  // suppresses toasts/errors in favor of the auto-save status indicator.
  const saveBalanceData = async (silent: boolean): Promise<void> => {
    if (!sessionToken || !currentDate) {
      if (!silent) showError('Missing session token or date');
      return;
    }

    try {
      const balanceData = [
        ...amData.map(row => ({
          employeeId: Number(row.employeeId) || 0,
          type: row.type,
          cashDue: Number(row.cashDue) || 0,
          shift: 'AM' as const,
          netSales: Number(row.netSales) || 0,
        })),
        ...pmData.map(row => ({
          employeeId: Number(row.employeeId) || 0,
          type: row.type,
          cashDue: Number(row.cashDue) || 0,
          shift: 'PM' as const,
          netSales: Number(row.netSales) || 0,
        })),
      ];

      const requestData = {
        date: currentDate,
        balance: balanceData,
        paid_outs: paidOuts.map(entry => ({
          description: entry.description,
          cashDue: Number(entry.cashDue) || 0,
        })),
        busser_tips: busserTips.map(entry => ({
          busserName: entry.busserName,
          tips: Number(entry.tips) || 0,
        })),
        event_servers_bartender_tips: eventServersBartenderTips.map(entry => ({
          eventServerBartender: entry.eventServerBartender,
          pooledTips: Number(entry.pooledTips) || 0,
        })),
        negative_cash_outs: negativeCashOuts.map(entry => ({
          tookFrom: entry.tookFrom,
          negativeCashOuts: Number(entry.negativeCashOuts) || 0,
        })),
        // Retired: Keno Cash tracking is now handled by the Register section's
        // "Keno" row, so this always saves empty (the backend field is still
        // required, but nothing populates or reads it on this page any more).
        keno_cash: [],
        register: REGISTER_DEFAULTS.map(r => ({
          registerType: r.key,
          employeeId: registerEmployeeForm[r.key] ? Number(registerEmployeeForm[r.key]) : null,
          closeOutAmount: parseFloat(registerForm[r.key]) || 0,
        })),
        bar_tips: barTips.map(entry => ({
          bartenderName: entry.bartenderName,
          tipAmount: Number(entry.tipAmount) || 0,
        })),
        expo_tips: expoTips.map(entry => ({
          expoName: entry.expoName,
          tipAmount: Number(entry.tipAmount) || 0,
        })),
      };

      await balanceApi.saveBalance(sessionToken, requestData);

      if (silent) {
        // Don't reload here - the fetch would race the next debounced auto-save if the
        // user keeps editing, and the data we just sent is already the latest state.
        setAutoSaveStatus('saved');
      } else {
        showSuccess('Balance data saved successfully');
        fetchBalanceData(currentDate);
      }
    } catch (error) {
      console.error('Error saving balance data:', error);
      if (silent) {
        setAutoSaveStatus('error');
        setAutoSaveError(error instanceof Error ? error.message : 'Failed to save changes');
      } else {
        showError('Failed to save balance data');
      }
    }
  };

  const performAutoSave = useCallback(async () => {
    setAutoSaveStatus('saving');
    await saveBalanceData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amData, pmData, paidOuts, busserTips, eventServersBartenderTips, negativeCashOuts, registerForm, registerEmployeeForm, barTips, expoTips, sessionToken, currentDate]);

  // Debounce a bulk auto-save ~900ms after any add/edit/delete across any of the
  // nine sections - but not the initial load from fetchBalanceData.
  useEffect(() => {
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      return;
    }
    if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    setAutoSaveStatus('pending');
    autoSaveTimeoutRef.current = setTimeout(() => {
      autoSaveTimeoutRef.current = null;
      performAutoSave();
    }, 900);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amData, pmData, paidOuts, busserTips, eventServersBartenderTips, negativeCashOuts, registerForm, registerEmployeeForm, barTips, expoTips]);

  const handleSave = async () => {
    setSaveLoading(true);
    try {
      await saveBalanceData(false);
    } finally {
      setSaveLoading(false);
    }
  };

  // Render simple table. Pass compact=true for smaller, narrower tables meant to
  // sit side-by-side (e.g. Busser/Bar/EXPO Tips).
  const renderSimpleTable = (
    title: string,
    columns: string[],
    data: any[],
    onAdd: () => void,
    onDelete: (id?: number) => void,
    form: any,
    setForm: any,
    formFields: { key: string; label: string; type?: string; hideLabel?: boolean; maxValue?: number; maxDisplayChars?: number }[],
    editingId: number | null,
    tempData: any,
    onStartEdit: (entry: any) => void,
    onSaveEdit: () => void,
    onCancelEdit: () => void,
    onTempDataChange: (field: string, value: any) => void,
    compact: boolean = false,
    showSubtotal: boolean = false,
    columnWidths?: string[],
    adminOnlyAddDelete: boolean = false,
    showDateNav: boolean = false
  ) => {
    const hdrCellSx = compact
      ? { color: '#fff', fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.03em', textTransform: 'uppercase' as const, border: '2px solid #7f0000', py: 0.75, px: 1 }
      : { color: '#fff', fontWeight: 700, fontSize: '0.82rem', letterSpacing: '0.04em', textTransform: 'uppercase' as const, border: '2px solid #7f0000', py: 1.5, px: 1.5 };

    // Map a formField key to the entry's actual field name (form uses snake_case, entries use camelCase)
    const mapFieldKeyToEntryKey = (key: string): string =>
      key === 'cashDue' ? 'cashDue' :
      key === 'busser_name' ? 'busserName' :
      key === 'tips' ? 'tips' :
      key === 'event_server_bartender' ? 'eventServerBartender' :
      key === 'pooled_tips' ? 'pooledTips' :
      key === 'took_from' ? 'tookFrom' :
      key === 'negative_cash_outs' ? 'negativeCashOuts' :
      key === 'bartender_name' ? 'bartenderName' :
      key === 'expo_name' ? 'expoName' :
      key === 'tip_amount' ? 'tipAmount' :
      key;

    const subtotalField = showSubtotal ? formFields.find(f => f.type === 'number') : undefined;
    const subtotalEntryKey = subtotalField ? mapFieldKeyToEntryKey(subtotalField.key) : null;
    const subtotalValue = subtotalEntryKey
      ? data.reduce((sum, entry) => sum + (Number(entry[subtotalEntryKey]) || 0), 0)
      : 0;

    return (
      <Box sx={{ mb: 3, ...(compact ? { flex: '1 1 0', minWidth: 260 } : {}) }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: compact ? 1 : 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
            {title}
          </Typography>
          {showDateNav && (
            <Box sx={{ display: 'flex', gap: 0.5, flex: '0 0 auto' }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleDateNav('prev')}
                disabled={dateLoading || dataLoading}
                sx={{ fontWeight: 700, color: '#2e7d32', borderColor: '#2e7d32', '&:hover': { borderColor: '#1b5e20', backgroundColor: 'rgba(46, 125, 50, 0.08)' } }}
              >
                Prev
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleDateNav('next')}
                disabled={dateLoading || dataLoading}
                sx={{ fontWeight: 700, color: '#2e7d32', borderColor: '#2e7d32', '&:hover': { borderColor: '#1b5e20', backgroundColor: 'rgba(46, 125, 50, 0.08)' } }}
              >
                Next
              </Button>
            </Box>
          )}
        </Box>
        <TableContainer component={Paper} sx={{ border: '2px solid #9e9e9e', width: '100%' }}>
          <Table size={compact ? 'small' : undefined} sx={{ borderCollapse: 'collapse', ...(columnWidths ? { tableLayout: 'fixed' } : {}), '& td': { border: '2px solid #9e9e9e', ...(compact ? { py: 0.5, px: 1 } : {}) } }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#b71c1c' }}>
                {columns.map((col, i) => (
                  <TableCell key={col} sx={{ ...hdrCellSx, ...(columnWidths ? { width: columnWidths[i] } : {}) }}>{col}</TableCell>
                ))}
                <TableCell align="center" sx={{ ...hdrCellSx, ...(columnWidths ? { width: columnWidths[columns.length] } : {}) }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((entry, idx) => {
                const isEditing = editingId === entry.id;
                const currentEntry = isEditing && tempData ? tempData : entry;
                return (
                  <TableRow key={entry.id || idx}>
                    {formFields.map((field, i) => {
                      const entryKey = mapFieldKeyToEntryKey(field.key);
                      const value = currentEntry[entryKey];
                      const entryKeyLower = entryKey.toLowerCase();
                      return (
                        <TableCell key={field.key} sx={columnWidths ? { width: columnWidths[i] } : undefined}>
                          {isEditing ? (
                            field.type === 'employeeName' ? (
                              <FormControl size="small" fullWidth>
                                <Select
                                  value={value || ''}
                                  onChange={(e) => onTempDataChange(entryKey, e.target.value)}
                                  displayEmpty
                                  disabled={employeesLoading}
                                >
                                  <MenuItem value=""><em>—</em></MenuItem>
                                  {employees.map((emp) => (
                                    <MenuItem key={emp.id} value={emp.name}>
                                      {emp.name} ({emp.employeeId || emp.id})
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            ) : (
                              <TextField
                                size="small"
                                type={field.type || 'text'}
                                value={value || ''}
                                onChange={(e) => {
                                  let val: string | number = field.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value;
                                  if (field.type === 'number' && field.maxValue !== undefined && Number(val) > field.maxValue) {
                                    val = field.maxValue;
                                  }
                                  onTempDataChange(entryKey, val);
                                }}
                                inputProps={field.type === 'number' ? { step: 0.01, ...(field.maxValue !== undefined ? { max: field.maxValue } : {}) } : {}}
                                InputProps={field.type === 'number' ? {
                                  startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography>
                                } : undefined}
                                fullWidth
                              />
                            )
                          ) : (
                            (entryKeyLower.includes('cash') || entryKeyLower.includes('tip') || entryKeyLower.includes('keno') || entryKeyLower.includes('negative') || entryKeyLower.includes('amount'))
                              ? `$${(Number(value) || 0).toFixed(2)}`
                              : (typeof value === 'number'
                                  ? value.toFixed(2)
                                  : (field.maxDisplayChars && typeof value === 'string' && value.length > field.maxDisplayChars
                                      ? `${value.slice(0, field.maxDisplayChars)}…`
                                      : (value || '')))
                          )}
                        </TableCell>
                      );
                    })}
                    <TableCell align="center" sx={columnWidths ? { width: columnWidths[formFields.length] } : undefined}>
                      {isEditing ? (
                        <>
                          <IconButton size="small" onClick={onSaveEdit} color="success">
                            <SaveIcon />
                          </IconButton>
                          <IconButton size="small" onClick={onCancelEdit} color="error">
                            <CancelIcon />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <IconButton size="small" onClick={() => onStartEdit(entry)} color="primary">
                            <EditIcon />
                          </IconButton>
                          {(!adminOnlyAddDelete || isAdmin) && (
                            <IconButton size="small" onClick={() => onDelete(entry.id)} color="error">
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {(!adminOnlyAddDelete || isAdmin) && (
              <TableRow>
                {formFields.map((field, i) => (
                  <TableCell key={field.key} sx={columnWidths ? { width: columnWidths[i] } : undefined}>
                    {field.type === 'employeeName' ? (
                      <FormControl size="small" fullWidth>
                        <InputLabel>{field.label}</InputLabel>
                        <Select
                          value={form[field.key]}
                          label={field.label}
                          onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}
                          disabled={employeesLoading}
                        >
                          {employees.map((emp) => (
                            <MenuItem key={emp.id} value={emp.name}>
                              {emp.name} ({emp.employeeId || emp.id})
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <TextField
                        size="small"
                        type={field.type || 'text'}
                        label={field.hideLabel ? undefined : field.label}
                        placeholder={field.hideLabel ? field.label : undefined}
                        value={form[field.key]}
                        onChange={(e) => {
                          let val = e.target.value;
                          if (field.type === 'number' && field.maxValue !== undefined && parseFloat(val) > field.maxValue) {
                            val = String(field.maxValue);
                          }
                          setForm({ ...form, [field.key]: val });
                        }}
                        inputProps={field.type === 'number' ? { step: 0.01, ...(field.maxValue !== undefined ? { max: field.maxValue } : {}) } : {}}
                        InputProps={field.type === 'number' ? {
                          startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography>
                        } : undefined}
                        fullWidth
                      />
                    )}
                  </TableCell>
                ))}
                <TableCell align="center" sx={columnWidths ? { width: columnWidths[formFields.length] } : undefined}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={onAdd}
                    disabled={formFields.some(field => !form[field.key])}
                  >
                    ADD
                  </Button>
                </TableCell>
              </TableRow>
              )}

              {showSubtotal && subtotalField && (
                <TableRow sx={{ backgroundColor: '#fde8e8', '& .MuiTableCell-root': { fontWeight: 700, color: '#7f0000', border: '2px solid #7f0000' } }}>
                  <TableCell colSpan={formFields.length - 1} align="right">Subtotal</TableCell>
                  <TableCell align="right">${subtotalValue.toFixed(2)}</TableCell>
                  <TableCell />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header with Date and Save Button */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Typography variant="h6" sx={{ color: '#7f0000', fontWeight: 700, flex: '0 0 auto', whiteSpace: 'nowrap' }}>
          Date: {dateLoading ? (
            <CircularProgress size={16} sx={{ ml: 1 }} />
          ) : currentDate ? (
            formatDateString(currentDate)
          ) : (
            new Date().toLocaleDateString('en-US', {
              timeZone: 'America/New_York',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })
          )}
        </Typography>
        <Typography variant="h4" component="h1" sx={{ flex: 1, textAlign: 'center', fontWeight: 700, textTransform: 'uppercase' }}>
          TIPS/Paid/Cash Outs
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: '0 0 auto' }}>
          <Typography
            variant="body2"
            color={autoSaveStatus === 'error' ? 'error' : 'text.secondary'}
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            {autoSaveStatus === 'saving' && (<><CircularProgress size={14} />Saving…</>)}
            {autoSaveStatus === 'pending' && 'Unsaved changes…'}
            {autoSaveStatus === 'saved' && 'All changes saved'}
            {autoSaveStatus === 'error' && (autoSaveError || 'Failed to save changes')}
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={handleSave}
            disabled={saveLoading}
            startIcon={saveLoading ? undefined : <SaveIcon />}
            sx={{ minWidth: 150 }}
          >
            {saveLoading ? <CircularProgress size={24} /> : 'SAVE DAILY'}
          </Button>
        </Box>
      </Box>

      {dataLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!dataLoading && (
        <>
          {/* Event Servers/Bartenders Tips */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            {renderSimpleTable(
              'Event Servers/Bartenders Tips',
              ['Event Servers/Bartenders', 'Pooled Tips'],
              eventServersBartenderTips,
              handleAddEventServerBartenderTip,
              handleDeleteEventServerBartenderTip,
              eventServerBartenderTipForm,
              setEventServerBartenderTipForm,
              [
                { key: 'event_server_bartender', label: 'Event Servers/Bartenders' },
                { key: 'pooled_tips', label: 'Pooled Tips', type: 'number', hideLabel: true },
              ],
              editingEventServerBartenderTip,
              tempEventServerBartenderTip,
              handleStartEditEventServerBartenderTip,
              handleSaveEditEventServerBartenderTip,
              handleCancelEditEventServerBartenderTip,
              (field: string, value: any) => setTempEventServerBartenderTip({ ...tempEventServerBartenderTip, [field]: value } as Partial<EventServerBartenderTipEntry>),
              true,
              true,
              undefined,
              false,
              true
            )}
          </Box>

          {/* Busser / Bar Tips - compact tables side by side */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            {renderSimpleTable(
              `Busser Tips - Tips Collected - $${tipOutTotals.bus.toFixed(2)}`,
              ["Busser's Name", 'Tip Amount'],
              busserTips,
              handleAddBusserTip,
              handleDeleteBusserTip,
              busserTipForm,
              setBusserTipForm,
              [
                { key: 'busser_name', label: "Busser's Name", type: 'employeeName', maxDisplayChars: 50 },
                { key: 'tips', label: 'Tip Amount', type: 'number', hideLabel: true, maxValue: 9999.99 },
              ],
              editingBusserTip,
              tempBusserTip,
              handleStartEditBusserTip,
              handleSaveEditBusserTip,
              handleCancelEditBusserTip,
              (field: string, value: any) => setTempBusserTip({ ...tempBusserTip, [field]: value } as Partial<BusserTipEntry>),
              true,
              true,
              ['55%', '20%', '25%'],
              true,
              true
            )}

            {renderSimpleTable(
              `Bar Tips - Tips Collected - $${tipOutTotals.bar.toFixed(2)}`,
              ['Bartender Name', 'Tip Amount'],
              barTips,
              handleAddBarTip,
              handleDeleteBarTip,
              barTipForm,
              setBarTipForm,
              [
                { key: 'bartender_name', label: 'Bartender Name', type: 'employeeName', maxDisplayChars: 50 },
                { key: 'tip_amount', label: 'Tip Amount', type: 'number', hideLabel: true, maxValue: 9999.99 },
              ],
              editingBarTip,
              tempBarTip,
              handleStartEditBarTip,
              handleSaveEditBarTip,
              handleCancelEditBarTip,
              (field: string, value: any) => setTempBarTip({ ...tempBarTip, [field]: value } as Partial<BarTipEntry>),
              true,
              true,
              ['55%', '20%', '25%'],
              true,
              true
            )}
          </Box>

          {/* EXPO Tips */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            {renderSimpleTable(
              `EXPO Tips - Tips Collected - $${tipOutTotals.expo.toFixed(2)}`,
              ['Expo Name', 'Tip Amount'],
              expoTips,
              handleAddExpoTip,
              handleDeleteExpoTip,
              expoTipForm,
              setExpoTipForm,
              [
                { key: 'expo_name', label: 'Expo Name', type: 'employeeName', maxDisplayChars: 50 },
                { key: 'tip_amount', label: 'Tip Amount', type: 'number', hideLabel: true, maxValue: 9999.99 },
              ],
              editingExpoTip,
              tempExpoTip,
              handleStartEditExpoTip,
              handleSaveEditExpoTip,
              handleCancelEditExpoTip,
              (field: string, value: any) => setTempExpoTip({ ...tempExpoTip, [field]: value } as Partial<ExpoTipEntry>),
              true,
              true,
              ['55%', '20%', '25%'],
              true,
              true
            )}
          </Box>

          {/* Paid Outs / Negative Cash Outs - side by side, below EXPO Tips */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            {renderSimpleTable(
              'Paid outs',
              ['Description', 'Cash Due'],
              paidOuts,
              handleAddPaidOut,
              handleDeletePaidOut,
              paidOutForm,
              setPaidOutForm,
              [
                { key: 'description', label: 'Description' },
                { key: 'cashDue', label: 'Cash Due', type: 'number', hideLabel: true },
              ],
              editingPaidOut,
              tempPaidOut,
              handleStartEditPaidOut,
              handleSaveEditPaidOut,
              handleCancelEditPaidOut,
              (field: string, value: any) => setTempPaidOut({ ...tempPaidOut, [field]: value } as Partial<PaidOutEntry>),
              true,
              false,
              ['55%', '20%', '25%'],
              false,
              true
            )}

            {renderSimpleTable(
              'Negative Cash Outs',
              ['Took from', 'Negative Cash Outs'],
              negativeCashOuts,
              handleAddNegativeCashOut,
              handleDeleteNegativeCashOut,
              negativeCashOutForm,
              setNegativeCashOutForm,
              [
                { key: 'took_from', label: 'Took from' },
                { key: 'negative_cash_outs', label: 'Negative Cash Outs', type: 'number', hideLabel: true },
              ],
              editingNegativeCashOut,
              tempNegativeCashOut,
              handleStartEditNegativeCashOut,
              handleSaveEditNegativeCashOut,
              handleCancelEditNegativeCashOut,
              (field: string, value: any) => setTempNegativeCashOut({ ...tempNegativeCashOut, [field]: value } as Partial<NegativeCashOutEntry>),
              true,
              false,
              ['55%', '20%', '25%'],
              false,
              true
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default TipsPaidCashOutsPage;
